/**
 * AI Structured Response Types
 *
 * Defines the canonical AI message envelope and structured payload system.
 *
 * These types support schema-driven rendering of rich AI responses - key-value
 * cards, summary sections, confidence indicators, and action footers - without
 * hardcoding per-agent or per-question UI.
 *
 * Design principles:
 * - Separate content format from content data
 * - Structured payloads are emitted by the backend as a WS event and persisted
 *   in chat_message.content_json
 * - Frontend renders generically via section type registry; no business logic
 *   lives in the renderer
 * - Actions are callbacks - the renderer emits an event; the parent handles it
 *
 * See context/50-ai/07-structured-responses.md for full design notes.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Runtime validation helpers
// ─────────────────────────────────────────────────────────────────────────────
const VALID_RESPONSE_TYPES = new Set([
    'key_value_card',
    'form_patch_proposal',
    'entity_enrichment',
    'summary_card',
    'custom',
]);
const VALID_SECTION_TYPES = new Set([
    'key_value_grid',
    'markdown',
    'text',
    'divider',
    'alert',
    'json_view',
    'document_preview',
]);
/**
 * Validate that an unknown value is a well-formed AiStructuredPayload.
 *
 * Used at the API boundary (history hydrator, WS event handler) to prevent
 * malformed content_json from crashing the chat renderer.
 *
 * Returns the typed payload if valid, null otherwise.
 */
function isValidSectionList(sections) {
    if (!Array.isArray(sections))
        return false;
    for (const section of sections) {
        if (typeof section !== 'object' || section === null)
            return false;
        const s = section;
        if (typeof s.type !== 'string' || !VALID_SECTION_TYPES.has(s.type))
            return false;
    }
    return true;
}
function isValidActionList(actions) {
    if (!Array.isArray(actions))
        return false;
    for (const action of actions) {
        if (typeof action !== 'object' || action === null)
            return false;
        const a = action;
        if (typeof a.id !== 'string' || typeof a.label !== 'string')
            return false;
    }
    return true;
}
export function parseAiStructuredPayload(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const obj = raw;
    if (typeof obj.type !== 'string' || !VALID_RESPONSE_TYPES.has(obj.type))
        return null;
    if (obj.sections !== undefined && !isValidSectionList(obj.sections))
        return null;
    if (obj.actions !== undefined && !isValidActionList(obj.actions))
        return null;
    // Validate cards if present (multi-card payload)
    if (obj.cards !== undefined) {
        if (!Array.isArray(obj.cards))
            return null;
        for (const card of obj.cards) {
            if (typeof card !== 'object' || card === null)
                return null;
            const c = card;
            if (typeof c.key !== 'string' || c.key.length === 0)
                return null;
            if (c.sections !== undefined && !isValidSectionList(c.sections))
                return null;
            if (c.actions !== undefined && !isValidActionList(c.actions))
                return null;
        }
    }
    return raw;
}
//# sourceMappingURL=ai-structured-response.js.map